// src/core/callback.js
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import registerRequestModel from '../api/models/register.request';
import loginRequest from '@/api/models/login.request';
import * as Storage from '@/core/storage.js';

export async function handleKeycloakCallback(store, router, route, registerUser, login) {
    const code = route.query.code;

    if (!code) {
        console.error('❌ No se encontró el parámetro "code" en la URL');
        return;
    }

    const hostname = window.location.hostname;

    // Defaults
    let clientId = process.env.VUE_APP_KEYCLOAK_CLIENTID;
    let clientSecret = process.env.VUE_APP_KEYCLOAK_CLIENT_SECRET;
    let urlBase = process.env.VUE_APP_KEYCLOAK_URL_BASE;
    let realm = process.env.VUE_APP_KEYCLOAK_REALM;

    // Override para dominio productivo
    if (hostname === 'tramites.gruposorah.com') {
        clientId = process.env.VUE_APP_KEYCLOAK_CLIENTID_TEST;
        clientSecret = process.env.VUE_APP_KEYCLOAK_CLIENT_SECRET_TEST;
        urlBase = process.env.VUE_APP_KEYCLOAK_URL_BASE;
        realm = process.env.VUE_APP_KEYCLOAK_REALM_TEST;
    }

    const redirectUri = `${window.location.origin}/callback`;

    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', clientId);
    params.append('client_secret', clientSecret);
    params.append('code', code);
    params.append('redirect_uri', redirectUri);

    try {
        const response = await axios.post(
            `${urlBase}/realms/${realm}/protocol/openid-connect/token`,
            params.toString(),
            {headers: {'Content-Type': 'application/x-www-form-urlencoded'}}
        );

        const data = response.data;

        // Tiempo de expiración real
        const issuedAt = Math.floor(Date.now() / 1000);
        const expiresAt = issuedAt + data.expires_in;

        if (!data.id_token) {
            console.error('❌ No se recibió id_token:', data);
            return;
        }

        const payload = jwtDecode(data.id_token);

        // Construimos User keycloak → objeto plano
        const user = {
            apellido_materno: payload.SecondLastName,
            apellido_paterno: payload.family_name,
            email: payload.email,
            curp: payload.curp,
            family_name: payload.family_name,
            given_name: payload.given_name,
            nombres: payload.given_name,
            rfc: payload.rfc,
            usuario: payload.email,
            email_verified: payload.email_verified,
            aud: payload.aud,
            avatar: payload.avatar || null,
        };

        // ---------------------------
        // Vuex: GUARDA userKeycloak SIN encrypt
        // y tokens ENCRYPTADOS mediante Storage
        // ---------------------------
        store.commit('keycloakModule/SET_USER', {user});
        store.commit('keycloakModule/SET_TOKENS', {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            idToken: data.id_token,
            token_expiration: data.expires_in,
            expiresAt,
        });

        // ---------------------------
        // Storage (tokens encriptados, user como objeto)
        // ---------------------------
        Storage.setItem('user', user, {encrypt: false});
        Storage.setItem('accessToken', data.access_token);   // encrypt:true default
        Storage.setItem('refreshToken', data.refresh_token);
        Storage.setItem('idToken', data.id_token);
        Storage.setItem('expiresAt', expiresAt);

        // Guardar userId directo
        if (payload.sub) {
            localStorage.setItem('userId', payload.sub);
        }

        // Timer refresh
        const refreshInMs = (data.expires_in - 60) * 1000;
        const timer = setTimeout(() => {
            store.dispatch('keycloakModule/refreshToken');
        }, refreshInMs);

        store.commit('keycloakModule/SET_REFRESH_TIMER', timer);

        // ---------------------------
        // LOGIN SUCCESS Vuex (también encripta tokens)
        // ---------------------------
        store.commit('account/loginSuccess', data);

        // ---------------------------
        // Registro en backend + Login
        // ---------------------------
        const registerPayload = mapKeyByRegister(payload);
        const loginPayload = mapLogByKey(payload);

        try {
            await registerUser(registerPayload);
        } catch (err) {
            console.warn('⚠️ Usuario ya existe en backend, continuando…');
        }

        await login(loginPayload);

        router.push('/process');

    } catch (error) {
        console.error('❌ Error al intercambiar código por token:', error);
    }
}

export function mapKeyByRegister(payload) {
    const model = registerRequestModel();

    model.usuario = payload.preferred_username || payload.usuario || '';
    model.email = payload.email || '';
    model.telefono = payload.phone_number || '';
    model.password = payload.pass;
    model.password_confirm = payload.pass;
    model.tipo_persona = 'fisica';
    model.user_type = 'username';
    model.curp = payload.curp || '';
    model.nombres = payload.given_name || payload.nombres || '';
    model.apellido_paterno = payload.family_name || payload.apellido_paterno || '';
    model.apellido_materno = payload.SecondLastName || '';
    model.rfc = payload.rfc || '';
    model.confirmado = 'si';
    model.client_id = process.env.VUE_APP_OAUTH20_CLIENT_ID;
    model.registrado = 1;
    model.estatus = 'activo';

    return model;
}

export function mapLogByKey(payload) {
    const model = loginRequest();

    model.username = payload.email;
    model.password = payload.pass;
    model.grant_type = 'password';

    return model;
}